import React, { useState, useEffect } from 'react';
import apiClient from '../api/client';
import { X, Users, ShieldCheck, Star, Trash2, UserPlus, Search } from 'lucide-react';

/**
 * DepartmentMembers modal — shows active members of a department,
 * lets admins add new members (from company users) and remove existing ones.
 * Requires manage_departments permission (enforced by backend).
 */
export default function DepartmentMembers({ department, onClose }) {
  const [members, setMembers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddPanel, setShowAddPanel] = useState(false);
  const [addSearch, setAddSearch] = useState('');
  const [addCanApprove, setAddCanApprove] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    fetchMembers();
    fetchAllUsers();
  }, [department.DepartmentID]);

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const res = await apiClient.get(`/departments/${department.DepartmentID}/users`);
      setMembers(res.data || []);
    } catch (err) {
      console.error('Fetch members error:', err);
      showMsg('Failed to load members', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const res = await apiClient.get('/users?page=1&pageSize=1000');
      setAllUsers(res.data?.items || []);
    } catch (err) {
      console.error('Fetch users error:', err);
    }
  };

  const handleAddMember = async () => {
    if (!selectedUserId) return;
    try {
      setSaving(true);
      await apiClient.post(`/departments/${department.DepartmentID}/users`, {
        userId: selectedUserId,
        canApprove: addCanApprove
      });
      showMsg('Member added', 'success');
      setSelectedUserId('');
      setAddCanApprove(false);
      setAddSearch('');
      setShowAddPanel(false);
      fetchMembers();
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to add member';
      showMsg(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveMember = async (userId, name) => {
    if (!confirm(`Remove ${name} from ${department.DepartmentName}?`)) return;
    try {
      await apiClient.delete(`/departments/${department.DepartmentID}/users/${userId}`);
      showMsg(`${name} removed`, 'success');
      fetchMembers();
    } catch (err) {
      showMsg('Failed to remove member', 'error');
    }
  };

  const showMsg = (text, type = 'info') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 4000);
  };

  // Users not already in this department
  const memberIds = new Set(members.map(m => m.UserID));
  const filteredAvailable = allUsers.filter(u => {
    if (memberIds.has(u.UserID)) return false;
    const q = addSearch.toLowerCase();
    return !q || u.FullName?.toLowerCase().includes(q) || u.Email?.toLowerCase().includes(q);
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 flex flex-col max-h-[85vh]">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between shrink-0">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Users className="w-5 h-5 text-gray-500" />
              {department.DepartmentName} — Members
            </h3>
            <p className="text-sm text-gray-500 mt-0.5">{members.length} active member{members.length !== 1 ? 's' : ''}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Message */}
        {message && (
          <div className={`mx-6 mt-3 p-3 rounded text-sm ${
            message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' :
            message.type === 'error' ? 'bg-red-50 text-red-800 border border-red-200' :
            'bg-blue-50 text-blue-800 border border-blue-200'
          }`}>
            {message.text}
          </div>
        )}

        {/* Member List */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="text-center text-gray-500 py-8">Loading...</div>
          ) : members.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              <Users className="w-10 h-10 mx-auto mb-2 text-gray-300" />
              <p>No members yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {members.map(m => (
                <div key={m.UserDepartmentID} className="flex items-center justify-between p-3 rounded border border-gray-200 hover:bg-gray-50">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 text-sm">{m.FullName || m.Email}</div>
                    <div className="text-xs text-gray-500">{m.Email}</div>
                    <div className="flex gap-2 mt-1">
                      {m.IsPrimary && (
                        <span className="flex items-center gap-1 px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">
                          <Star className="w-3 h-3" /> Primary
                        </span>
                      )}
                      {m.CanApprove && (
                        <span className="flex items-center gap-1 px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded">
                          <ShieldCheck className="w-3 h-3" /> Can Approve
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => handleRemoveMember(m.UserID, m.FullName || m.Email)}
                    className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors ml-2"
                    title="Remove from department"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Add Member Panel */}
        <div className="border-t border-gray-200 px-6 py-4 shrink-0">
          {!showAddPanel ? (
            <button
              onClick={() => setShowAddPanel(true)}
              className="btn btn-primary btn-sm w-full flex items-center justify-center gap-2"
            >
              <UserPlus className="w-4 h-4" /> Add Member
            </button>
          ) : (
            <div className="space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search users..."
                  value={addSearch}
                  onChange={(e) => { setAddSearch(e.target.value); setSelectedUserId(''); }}
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div className="max-h-36 overflow-y-auto border border-gray-200 rounded-lg">
                {filteredAvailable.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-3">No matching users</p>
                ) : (
                  filteredAvailable.slice(0, 50).map(u => (
                    <label key={u.UserID} className="flex items-center p-2 hover:bg-gray-50 cursor-pointer">
                      <input
                        type="radio"
                        name="addUser"
                        value={u.UserID}
                        checked={selectedUserId === u.UserID}
                        onChange={() => setSelectedUserId(u.UserID)}
                        className="mr-2"
                      />
                      <div>
                        <div className="text-sm font-medium text-gray-900">{u.FullName || u.Email}</div>
                        <div className="text-xs text-gray-500">{u.Email}</div>
                      </div>
                    </label>
                  ))
                )}
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={addCanApprove}
                  onChange={(e) => setAddCanApprove(e.target.checked)}
                  className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                />
                <ShieldCheck className="w-4 h-4 text-green-600" />
                Can approve invoices in this department
              </label>
              <div className="flex gap-2">
                <button
                  onClick={() => { setShowAddPanel(false); setSelectedUserId(''); setAddSearch(''); setAddCanApprove(false); }}
                  className="btn btn-secondary btn-sm flex-1"
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddMember}
                  className="btn btn-primary btn-sm flex-1"
                  disabled={!selectedUserId || saving}
                >
                  {saving ? 'Adding...' : 'Add'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
